import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ContentType, LicenseType, PrivateCategoryResponse } from '../../../interface';
import { useHistory } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { BreadCrumbView, BreadcrumbLink } from '../../components/breadcrumb';
import { HibraryRootState } from '../../../redux/rootReducer';
import { action } from '../../redux/request';
import { getListCategoryRoute, getListSubCategoryRoute } from '../../routes/generatePath';
import { removeSplash } from '../../helpers/removeSplash';
import { Helmet } from 'react-helmet-async';
import { Loading } from '../../customUI/loading';
import { SegmentHeader } from '../home/segmentHeader';
import { getCategoryContents } from '../../redux/store/storeCrud';
import ContentWithDetailView from '../../components/segment/contentWithDetailView';
import PodcastSlideView from '../../components/segment/podcastSlideView';
import VideoSlideView from '../../components/segment/videoSlideView';

interface CategoryContentViewProps {
    categoryUID: string;
    licenseType: LicenseType;
    contentType: ContentType;
}

const PrivateCategoryContentPage = (props: CategoryContentViewProps) => {
    const pageId = 'CategoryContentView';
    const dispatch = useDispatch();
    const history = useHistory();
    const [categoryContents, setCategoryContents] = useState<PrivateCategoryResponse>();
    const [breadCrumbs, setBreadCrumbs] = useState<BreadcrumbLink[]>();
    const { clientInfo, authen } = useSelector(
        (state: HibraryRootState) => ({
            clientInfo: state.clientInfo,
            authen: state.auth,
            environment: state.environment
        }),
        shallowEqual
    );

    useEffect(() => {
        fetchData();
    }, []);

    const breadcrumbMemo = useMemo(() => {
        return <BreadCrumbView addLinks={breadCrumbs} />;
    }, [breadCrumbs]);

    const fetchData = async () => {
        try {
            dispatch(action.setProcess(true, pageId));
            const response = await getCategoryContents<PrivateCategoryResponse>(
                authen.isLogin,
                clientInfo.companyPrefix,
                props.licenseType,
                props.categoryUID,
                props.contentType
            );

            if (response) {
                if (response.subcats.length > 1) setCategoryContents(response);
                else
                    history.push(
                        getListSubCategoryRoute(
                            props.categoryUID,
                            response.subcats[0].subcatId,
                            props.licenseType,
                            props.contentType
                        )
                    );

                setBreadCrumbs([
                    {
                        title: 'หมวดหมู่',
                        path: getListCategoryRoute(props.licenseType, props.contentType)
                    },
                    { title: response?.catName ?? '' }
                ]);
                dispatch(action.setProcess(false, pageId));
                dispatch(removeSplash);
            }
        } catch (error) {
            dispatch(action.setProcess(false, pageId));
            dispatch(removeSplash);
        }
    };

    return (
        <div className='store'>
            <Helmet>
                <title>
                    {window.location.hostname.match(/cu-elibrary.com/)
                        ? process.env.REACT_APP_CU_ELIBRARY_APP_NAME
                        : 'Hibrary'}{' '}
                    | หมวดหมู่ : {`${categoryContents?.catName ?? ''}`}
                </title>
            </Helmet>
            {breadcrumbMemo}
            <h1 className='viewAllTitle'>{`${categoryContents?.catName ?? ''}`}</h1>
            <div className='segment-list'>
                {categoryContents?.subcats.map((el, index) => (
                    <>
                        {el.ebooks && el.ebooks.length > 0 && (
                            <div key={index}>
                                <SegmentHeader
                                    title={el.subcatName}
                                    path={getListSubCategoryRoute(
                                        props.categoryUID,
                                        el.subcatId,
                                        props.licenseType,
                                        props.contentType
                                    )}
                                    right={el.ebooks.length > 7 ? true : false}
                                />
                                <ContentWithDetailView contents={el.ebooks ?? []} />
                            </div>
                        )}

                        {el.podcasts && el.podcasts.length > 0 && (
                            <div key={index}>
                                <SegmentHeader
                                    title={el.subcatName}
                                    path={getListSubCategoryRoute(
                                        props.categoryUID,
                                        el.subcatId,
                                        props.licenseType,
                                        props.contentType
                                    )}
                                    right={(el.podcasts?.length ?? 0) > 7 ? true : false}
                                />
                                <PodcastSlideView
                                    podcasts={el.podcasts}
                                    licenseType={props.licenseType}
                                />
                            </div>
                        )}

                        {el.videos && el.videos.length > 0 && (
                            <div key={index}>
                                <SegmentHeader
                                    title={el.subcatName}
                                    path={getListSubCategoryRoute(
                                        props.categoryUID,
                                        el.subcatId,
                                        props.licenseType,
                                        props.contentType
                                    )}
                                    right={el.videos.length > 7 ? true : false}
                                />
                                <VideoSlideView
                                    videos={el.videos}
                                    licenseType={props.licenseType}
                                />
                            </div>
                        )}
                    </>
                ))}
            </div>
            <Loading id={pageId} />
        </div>
    );
};

export default PrivateCategoryContentPage;
